import React from "react";
import { formatDate } from "../../utils/dateUtils";
import ActionMenu from "../UsersView/ActionMenu";
/**
 * Extracts cell value from user object based on column key
 * @param {Object} user - User object
 * @param {string} columnKey - Column key identifier
 * @returns {string} Extracted value
 */
export function extractCellValue(session, columnKey) {
  switch (columnKey) {
    case "sessionName": {
      return session.sessionName || "—";
    }
    case "sessionID": {
      return session.sessionID || session.id || "—";
    }
    case "sessionUserId": {
      return session.sessionUserId || "—";
    }
    case "parentUserId": {
      return session.parentUserId || "—";
    }
    case "sessionDate": {
      return session.sessionDate ? formatDate(session.sessionDate) : "—";
    }
    case "sessionEnv": {
      return session.sessionEnv || "—";
    }
    case "sessionPurpose": {
      return session.sessionPurpose || "—";
    }
    case "sessionType": {
      return session.sessionType || "—";
    }
    case "weatherDesc": {
      return session.weatherDesc || "—";
    }
    case "windSpeed": {
      return session.windSpeed !== null && session.windSpeed !== undefined
        ? session.windSpeed
        : "—";
    }
    case "location": {
      return session.location || "—";
    }
    case "temperature": {
      return session.temperature !== null && session.temperature !== undefined
        ? session.temperature
        : "—";
    }
    case "videoIds": {
      return session.videoIds !== null && session.videoIds !== undefined
        ? session.videoIds.join(", ")
        : "—";
    }
    case "score": {
      return session.score !== null && session.score !== undefined
        ? session.score
        : "—";
    }
    default:
      return "—";
  }
}
/**
 * Creates column definitions for the admin search table
 * @returns {Object} Column definitions object
 */
export function createSessionsColumnDefinitions() {
  const renderHeader = (label) => <th className="px-2 py-3">{label}</th>;

  return {
    actions: {
      key: "actions",
      label: "Actions",
      renderHeader: () => <th className="px-0.5 py-3 text-center"></th>,
      renderCell: (session, handlers) => (
        <td className="px-0.5 py-3 text-center">
          <ActionMenu onEdit={() => handlers?.onEditSession?.(session.id)} />
        </td>
      ),
    },
    sessionName: {
      key: "sessionName",
      label: "Session Name",
      renderHeader: () => renderHeader("Session Name"),
      renderCell: (session) => (
        <td className="px-2 py-3">
          {extractCellValue(session, "sessionName")}
        </td>
      ),
    },
    sessionID: {
      key: "sessionID",
      label: "Session ID",
      renderHeader: () => renderHeader("Session ID"),
      renderCell: (session) => (
        <td className="px-2 py-3">{extractCellValue(session, "sessionID")}</td>
      ),
    },
    sessionUserId: {
      key: "sessionUserId",
      label: "Session User ID",
      renderHeader: () => renderHeader("Session User ID"),
      renderCell: (session) => (
        <td className="px-2 py-3">
          {extractCellValue(session, "sessionUserId")}
        </td>
      ),
    },
    parentUserId: {
      key: "parentUserId",
      label: "Parent User ID",
      renderHeader: () => renderHeader("Parent User ID"),
      renderCell: (session) => (
        <td className="px-2 py-3">
          {extractCellValue(session, "parentUserId")}
        </td>
      ),
    },
    sessionDate: {
      key: "sessionDate",
      label: "Session Date",
      renderHeader: () => renderHeader("Session Date"),
      renderCell: (session) => (
        <td className="px-2 py-3">
          {extractCellValue(session, "sessionDate")}
        </td>
      ),
    },
    sessionEnv: {
      key: "sessionEnv",
      label: "Environment",
      renderHeader: () => renderHeader("Environment"),
      renderCell: (session) => (
        <td className="px-2 py-3">{extractCellValue(session, "sessionEnv")}</td>
      ),
    },
    sessionPurpose: {
      key: "sessionPurpose",
      label: "Purpose",
      renderHeader: () => renderHeader("Purpose"),
      renderCell: (session) => (
        <td className="px-2 py-3">
          {extractCellValue(session, "sessionPurpose")}
        </td>
      ),
    },
    sessionType: {
      key: "sessionType",
      label: "Type",
      renderHeader: () => renderHeader("Type"),
      renderCell: (session) => (
        <td className="px-2 py-3">
          {extractCellValue(session, "sessionType")}
        </td>
      ),
    },
    weatherDesc: {
      key: "weatherDesc",
      label: "Weather",
      renderHeader: () => renderHeader("Weather"),
      renderCell: (session) => (
        <td className="px-2 py-3">
          {extractCellValue(session, "weatherDesc")}
        </td>
      ),
    },
    windSpeed: {
      key: "windSpeed",
      label: "Wind Speed",
      renderHeader: () => renderHeader("Wind Speed"),
      renderCell: (session) => (
        <td className="px-2 py-3">{extractCellValue(session, "windSpeed")}</td>
      ),
    },
    location: {
      key: "location",
      label: "Location",
      renderHeader: () => renderHeader("Location"),
      renderCell: (session) => (
        <td className="px-2 py-3">{extractCellValue(session, "location")}</td>
      ),
    },
    temperature: {
      key: "temperature",
      label: "Temperature",
      renderHeader: () => renderHeader("Temperature"),
      renderCell: (session) => (
        <td className="px-2 py-3">
          {extractCellValue(session, "temperature")}
        </td>
      ),
    },
    videoIds: {
      key: "videoIds",
      label: "Video IDs",
      renderHeader: () => renderHeader("Video IDs"),
      renderCell: (session) => {
        return (
          <td className="px-2 py-3">{extractCellValue(session, "videoIds")}</td>
        );
      },
    },
    score: {
      key: "score",
      label: "Score",
      renderHeader: () => renderHeader("Score"),
      renderCell: (session) => (
        <td className="px-2 py-3">{extractCellValue(session, "score")}</td>
      ),
    },
  };
}
